import { Fragment, useCallback, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { Topbar } from "@/components/Topbar";
import { Button } from "@/components/Button";
import {
  AiNavigation,
  count,
  dateTime,
  LoadState,
  Pager,
  tableClass,
} from "@/components/ControlCenter";
import {
  AiReportFilters,
  billingLabel,
  latency,
  nullableUsd,
} from "@/components/AiReportFilters";
import {
  controlCenterService,
  ControlFilters,
  RequestRow,
} from "@/services/control-center.service";
import { useAdminResource } from "@/hooks/useAdminResource";

export default function AiRequestsPage() {
  const [search] = useSearchParams();
  const [filters, setFilters] = useState<ControlFilters>(() => ({
    ...Object.fromEntries(
      ["companyUid", "provider", "model", "feature", "billingMode", "status"]
        .filter((key) => search.get(key))
        .map((key) => [key, search.get(key)!]),
    ),
    ...(search.get("period") === "today"
      ? { period: "today" }
      : search.get("days") && [7, 30, 90].includes(Number(search.get("days")))
        ? { days: Number(search.get("days")) }
        : { period: "month" }),
    page: 1,
  }));
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data, loading, error, refresh } = useAdminResource(
    useCallback(() => controlCenterService.requests(filters), [filters]),
  );
  return (
    <>
      <Topbar
        title="AI request logs"
        breadcrumb="AI Control Center / Request Logs"
        right={
          <Button
            variant="ghost"
            size="sm"
            loading={loading}
            onClick={refresh}
            iconLeft={<RefreshCw size={14} />}
          >
            Refresh
          </Button>
        }
      />
      <div className="admin-page space-y-4">
        <AiNavigation />
        <p className="text-sm text-text-secondary">
          Usage, charges and request health. Customer prompts, responses and
          credentials stay private.
        </p>
        <AiReportFilters
          value={filters}
          onChange={(value) => {
            setExpanded(null);
            setFilters(value);
          }}
          withStatus
        />
        <LoadState
          loading={loading}
          error={error}
          hasData={!!data}
          retry={refresh}
        />
        {data && (
          <section className="admin-panel overflow-hidden">
            <div className="overflow-x-auto">
              <table className={tableClass}>
                <thead className="bg-surface-alt">
                  <tr>
                    <th>
                      <span className="sr-only">Details</span>
                    </th>
                    <th>Time / status</th>
                    <th>Company</th>
                    <th>Provider / model</th>
                    <th>Feature</th>
                    <th>Billing mode</th>
                    <th>Input / output</th>
                    <th>Provider cost</th>
                    <th>Credits</th>
                    <th>Latency</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <Fragment key={row._id}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            aria-label={`${expanded === row._id ? "Hide" : "Show"} request details`}
                            aria-expanded={expanded === row._id}
                            aria-controls={`request-${row._id}`}
                            onClick={() =>
                              setExpanded(expanded === row._id ? null : row._id)
                            }
                            className="rounded p-1 text-text-secondary hover:bg-primary-soft hover:text-primary"
                          >
                            {expanded === row._id ? (
                              <ChevronDown size={16} />
                            ) : (
                              <ChevronRight size={16} />
                            )}
                          </button>
                        </td>
                        <td>
                          <span className="block text-text-muted">
                            {dateTime(row.createdAt)}
                          </span>
                          <span
                            className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] capitalize ${row.status === "failed" ? "bg-danger-soft text-danger" : row.status === "pending" ? "bg-warning-soft text-warning" : "bg-primary-soft text-primary"}`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td>
                          <Link
                            className="font-medium hover:text-primary"
                            to={`/companies/${row.companyUid}`}
                          >
                            {row.companyName}
                          </Link>
                        </td>
                        <td>
                          <span className="block capitalize">
                            {row.provider}
                          </span>
                          <span className="text-[11px] text-text-muted">
                            {row.model}
                          </span>
                        </td>
                        <td>
                          {row.feature}
                          <span className="block text-[10px] capitalize text-text-muted">
                            {row.requestType ?? "Legacy request"}
                          </span>
                        </td>
                        <td>{billingLabel(row.billingMode)}</td>
                        <td>
                          {count(row.tokensIn)} / {count(row.tokensOut)}
                        </td>
                        <td>
                          {nullableUsd(row.costUsd)}
                          {row.billingMode === "byok" && (
                            <span className="block text-[10px] text-text-muted">
                              Company key · external cost
                            </span>
                          )}
                        </td>
                        <td>{count(row.creditsCharged)}</td>
                        <td>{latency(row.latencyMs)}</td>
                      </tr>
                      {expanded === row._id && (
                        <tr id={`request-${row._id}`}>
                          <td
                            colSpan={10}
                            className="!whitespace-normal bg-surface-alt"
                          >
                            <RequestDetails row={row} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {!data.rows.length && (
              <p className="p-8 text-center text-sm text-text-muted">
                No recorded requests match these filters.
              </p>
            )}
            <Pager
              {...data}
              onChange={(page) => {
                setExpanded(null);
                setFilters({ ...filters, page });
              }}
              loading={loading}
            />
          </section>
        )}
      </div>
    </>
  );
}
function RequestDetails({ row }: { row: RequestRow }) {
  const items = [
    ["Request ID", row.usageUid ?? row._id],
    ["User", row.userUid ?? "Not recorded"],
    ["Rate card", row.priceUid ?? "Not recorded"],
    ["Cost status", row.costStatus],
    ["Platform cost", nullableUsd(row.platformCostUsd)],
    ["Billed credit value", nullableUsd(row.customerChargeUsd)],
    ["Gross margin estimate", nullableUsd(row.grossMarginUsd)],
    ["Retries", count(row.retryCount ?? 0)],
    ["Cached input tokens", count(row.cachedInputTokens ?? 0)],
    ["Cache-read tokens", count(row.cacheReadTokens ?? 0)],
    ["Cache-write tokens", count(row.cacheWriteTokens ?? 0)],
    ["Reasoning tokens", count(row.reasoningTokens ?? 0)],
    ["Images", count(row.imagesGenerated ?? 0)],
    ["Video seconds", count(row.videoSecondsGenerated ?? 0)],
    ["Audio input seconds", count(row.audioInputSeconds ?? 0)],
    ["Audio output seconds", count(row.audioOutputSeconds ?? 0)],
    ["Speech characters", count(row.characters ?? 0)],
  ];
  return (
    <div className="space-y-3 py-2">
      {row.status === "pending" && (
        <p className="rounded-lg border border-warning/20 bg-warning-soft p-3 text-warning">
          {count(
            (row.reservedIncludedCredits ?? 0) +
              (row.reservedTopupCredits ?? 0),
          )}{" "}
          credits reserved · {nullableUsd(row.reservedCostUsd)} estimated
          provider cost. A pending request may still be running or need
          settlement review; check the provider before replaying it.
        </p>
      )}
      {row.errorCategory && (
        <p className="text-danger">
          Failure category: {row.errorCategory.replace(/_/g, " ")}
        </p>
      )}
      {!!row.unfundedCredits && (
        <p className="text-warning">
          {count(row.unfundedCredits)} credits exceeded the available balance at
          settlement.
        </p>
      )}
      <dl className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {items.map(([label, value]) => (
          <div key={label}>
            <dt className="text-[11px] text-text-muted">{label}</dt>
            <dd className="mt-1 break-all font-medium">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
